import React from 'react';
import { Box, Button, Container, Flex, Heading, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useLocation } from 'react-router-dom';

export const OPERATORS_FB_GROUP = 'https://www.facebook.com/groups/1141717998392158';

export const GUIDE_LINKS = [
  { href: '/slb/get-customers', label: 'Start here' },
  { href: '/slb/get-customers/seo', label: 'Free SEO' },
  { href: '/slb/get-customers/google-ads', label: 'Google Ads' },
  { href: '/slb/get-customers/facebook-ads', label: 'Facebook and Instagram' },
  { href: '/slb/get-customers/campaigns', label: 'Campaign plans' },
];

const SITE = 'https://www.smartlaundrybasket.ai';

export function GuideLayout({ title, description, path, children }) {
  const { pathname } = useLocation();
  const canonical = `${SITE}${path}`;
  return (
    <Box bg="white" minH="100vh">
      <Helmet>
        <title>{title} | Smart Laundry Basket</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <Box as="nav" position="sticky" top={0} zIndex={100} bg="white" borderBottom="1px solid" borderColor="gray.100" boxShadow="sm">
        <Container maxW="900px">
          <Flex py={3} align="center" justify="space-between" gap={3}>
            <Box as="a" href="/slb">
              <Image src="/slb-logo.png" alt="Smart Laundry Basket" h={{ base: '36px', md: '44px' }} objectFit="contain" />
            </Box>
            <Button as="a" href="/quickstart" size="sm" colorScheme="orange" borderRadius="full">Start free</Button>
          </Flex>
        </Container>
      </Box>

      <Container maxW="900px" py={{ base: 8, md: 12 }}>
        <HStack spacing={2} mb={8} flexWrap="wrap">
          {GUIDE_LINKS.map((item) => {
            const on = pathname === item.href;
            return (
              <Button
                key={item.href}
                as={RouterLink}
                to={item.href}
                size="sm"
                borderRadius="full"
                variant={on ? 'solid' : 'outline'}
                colorScheme={on ? 'orange' : 'gray'}
              >
                {item.label}
              </Button>
            );
          })}
        </HStack>
        {children}
      </Container>

      <Box py={8} bg="gray.900" color="gray.400">
        <Container maxW="900px">
          <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" gap={3}>
            <Text fontSize="sm">For gig workers running wash and fold pickup.</Text>
            <HStack spacing={4} fontSize="sm">
              <Text as="a" href={OPERATORS_FB_GROUP} target="_blank" rel="noopener noreferrer" _hover={{ color: 'white' }}>Operators group</Text>
              <Text as="a" href="/slb#gig-workers" _hover={{ color: 'white' }}>Product</Text>
              <Text as="a" href="/quickstart" _hover={{ color: 'white' }}>Start free</Text>
            </HStack>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
}

export function GuideTitle({ kicker, heading, lede }) {
  return (
    <VStack align="stretch" spacing={3} mb={8}>
      <Text fontSize="xs" fontWeight="800" color="orange.600" textTransform="uppercase" letterSpacing="wide">{kicker}</Text>
      <Heading as="h1" fontSize={{ base: '2xl', md: '4xl' }} lineHeight="shorter">{heading}</Heading>
      <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.700">{lede}</Text>
    </VStack>
  );
}

export function Steps({ items }) {
  return (
    <VStack align="stretch" spacing={3}>
      {items.map(([title, body], index) => (
        <HStack key={title} align="start" bg="gray.50" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={4} spacing={4}>
          <Flex w="32px" h="32px" borderRadius="full" bg="orange.500" color="white" align="center" justify="center" fontWeight="800" flexShrink={0}>
            {index + 1}
          </Flex>
          <Box>
            <Text fontWeight="700">{title}</Text>
            <Text fontSize="sm" color="gray.700" mt={1} whiteSpace="pre-wrap">{body}</Text>
          </Box>
        </HStack>
      ))}
    </VStack>
  );
}

export function CopyBlock({ label, children }) {
  return (
    <Box mb={6}>
      <Text fontSize="sm" fontWeight="700" mb={2}>{label}</Text>
      <Box as="pre" whiteSpace="pre-wrap" fontFamily="body" fontSize="sm" bg="gray.900" color="white" borderRadius="xl" p={4}>
        {children}
      </Box>
    </Box>
  );
}

export function Section({ title, children }) {
  return (
    <Box as="section" mb={10}>
      <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} mb={3}>{title}</Heading>
      {children}
    </Box>
  );
}
